import { venueZone } from "./format.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foldBatch, type ChatMessage, type ChatThread } from "@baros/core";
import { api } from "./api.js";
import { Chat } from "./Chat.js";
import { chatApi } from "./chat-api.js";
import { useApp } from "./context.js";
import { NoVenue, useActingVenue } from "./NoVenue.js";
import type { ScreenProps } from "./screens.js";

/**
 * Screen 04. The development chat: the organisation's shared threads down
 * the side, one open (#75).
 *
 *   params.chatId   the thread to open
 *   params.specId   the drink a new thread is about — and, without `fresh`,
 *                   a request to open that drink's latest thread, which is
 *                   what "Revise in development" on a recipe means
 *   params.fresh    start a new thread even when the drink has one
 *   params.prompt   a question already typed: the flavour wheel's tray
 *
 * Opening another thread is a navigation like any other, so the screen
 * mounts afresh for it and the back button returns to the one before. A
 * thread started mid-turn only rewrites the address, so the turn streaming
 * into it is not torn down.
 */
export function ChatScreen({ params }: ScreenProps) {
  const app = useApp();
  const venueId = useActingVenue();
  const { token, go } = app;

  const [threads, setThreads] = useState<readonly ChatThread[] | null>(null);
  /** What the open thread holds, as read when the screen opened. */
  const [opened, setOpened] = useState<{
    readonly thread: ChatThread | null;
    readonly history: readonly ChatMessage[];
    readonly mayDelete: boolean;
  } | null>(params.chatId ? null : { thread: null, history: [], mayDelete: false });
  /** The open thread as it is now: a turn renames nothing but bumps its date. */
  const [current, setCurrent] = useState<ChatThread | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  // Open by default only where there is room for it beside the menu, the
  // conversation and the draft. On a tablet in landscape — an iPad, a Lenovo
  // Tab M11 at 1280 — it left the conversation about 460px; the Chats
  // button opens it when it is wanted.
  const [listOpen, setListOpen] = useState(() => window.matchMedia("(min-width: 1400px)").matches);

  const openThread = useCallback(
    async (chatId: string) => {
      try {
        const res = await chatApi.open(token, chatId);
        setOpened({ thread: res.chat, history: res.messages, mayDelete: res.mayDelete });
        setCurrent(res.chat);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not open that chat");
        setOpened({ thread: null, history: [], mayDelete: false });
      }
    },
    [token],
  );

  // Once, on opening: the list, and the thread the address names.
  useEffect(() => {
    let live = true;
    void chatApi
      .list(token)
      .then(async ({ chats }) => {
        if (!live) return;
        setThreads(chats);
        if (params.chatId) {
          await openThread(params.chatId);
        } else if (params.specId && !params.fresh) {
          // "Revise in development": pick up the drink's latest thread.
          const latest = chats.find((t) => t.specId === params.specId);
          if (latest) {
            go({ kind: "chat", params: { chatId: latest.chatId, ...(params.prompt ? { prompt: params.prompt } : {}) } }, { replace: true });
            await openThread(latest.chatId);
          }
        }
      })
      .catch((err: unknown) => {
        if (!live) return;
        setThreads([]);
        setError(err instanceof Error ? err.message : "Could not read the chats");
        if (params.chatId) setOpened({ thread: null, history: [], mayDelete: false });
      });
    return () => {
      live = false;
    };
    // Mount only: another thread is another navigation, which remounts.
  }, []);

  const upsert = useCallback((thread: ChatThread) => {
    setThreads((list) => [thread, ...(list ?? []).filter((t) => t.chatId !== thread.chatId)]);
  }, []);

  const specNames = useMemo(
    () => new Map(app.library.specs.map((s) => [s.specId, s.name])),
    [app.library.specs],
  );

  // A turn is costed and logged against a venue, so there is nothing to
  // develop against until there is one. Which of the two reasons that is,
  // and whose job the fix is, `NoVenue` works out.
  if (!venueId) return <NoVenue what="the development chat" />;

  const thread = current ?? opened?.thread ?? null;
  const subjectId = thread ? (thread.specId ?? undefined) : params.specId;
  const subject = subjectId ? app.library.specs.find((s) => s.specId === subjectId) : undefined;
  const activeId = thread?.chatId ?? null;

  const newChat = (specId?: string) =>
    go({ kind: "chat", params: { ...(specId ? { specId } : {}), fresh: "1" } });

  async function rename(chatId: string) {
    setBusy(true);
    setError(null);
    try {
      const { chat } = await chatApi.rename(token, chatId, title);
      setThreads((list) => (list ?? []).map((t) => (t.chatId === chatId ? chat : t)));
      if (chat.chatId === activeId) setCurrent(chat);
      setRenaming(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not rename that chat");
    } finally {
      setBusy(false);
    }
  }

  async function remove(t: ChatThread) {
    if (!window.confirm(`Delete "${t.title}"? It goes for everyone in the organisation, and cannot be brought back.`)) return;
    setBusy(true);
    setError(null);
    try {
      await chatApi.remove(token, t.chatId);
      setThreads((list) => (list ?? []).filter((x) => x.chatId !== t.chatId));
      // The open one went: start afresh rather than show a thread that is not there.
      if (t.chatId === activeId) newChat();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete that chat");
    } finally {
      setBusy(false);
    }
  }

  const mayDelete = (t: ChatThread) =>
    t.createdBy === app.me.userId || app.me.can["spec:edit"] || (t.chatId === activeId && opened?.mayDelete === true);

  return (
    <div className={`chat-shell${listOpen ? " list-open" : ""}`}>
      <aside className="chat-threads" aria-label="Chats">
        <div className="chat-threads-head">
          <span className="label">Chats{threads ? ` · ${threads.length}` : ""}</span>
          <span className="spacer" />
          <button className="btn sm" onClick={() => newChat()}>+ New chat</button>
        </div>
        {error && <div className="msg err" style={{ margin: 10 }}>{error}</div>}
        <div className="scroll keep-scroll chat-thread-list">
          {threads === null && <div className="label pad">Reading the chats…</div>}
          {threads?.length === 0 && (
            <div className="muted pad" style={{ fontSize: 13, lineHeight: 1.5 }}>
              No chats yet. Ask something and it is kept here for the whole team.
            </div>
          )}
          {threads?.map((t) => (
            <div key={t.chatId} className="chat-thread" aria-current={t.chatId === activeId}>
              {renaming === t.chatId ? (
                <form
                  className="chat-thread-rename"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void rename(t.chatId);
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    maxLength={80}
                    aria-label="Chat name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Escape" && setRenaming(null)}
                  />
                  <button className="btn sm" type="submit" disabled={busy || !title.trim()}>Save</button>
                  <button className="btn sm" type="button" onClick={() => setRenaming(null)}>Cancel</button>
                </form>
              ) : (
                <>
                  <button
                    className="chat-thread-open"
                    onClick={() => {
                      if (t.chatId !== activeId) go({ kind: "chat", params: { chatId: t.chatId } });
                      if (!window.matchMedia("(min-width: 901px)").matches) setListOpen(false);
                    }}
                  >
                    <span className="t">{t.title}</span>
                    <span className="s">
                      {t.specId ? `${specNames.get(t.specId) ?? t.specId} · ` : ""}
                      {ago(t.updatedAt)} · {t.updatedBy === app.me.userId ? "you" : t.updatedByName}
                    </span>
                  </button>
                  <span className="chat-thread-actions">
                    <button
                      className="btn sm"
                      title="Rename"
                      aria-label={`Rename ${t.title}`}
                      disabled={busy}
                      onClick={() => {
                        setRenaming(t.chatId);
                        setTitle(t.title);
                      }}
                    >
                      ✎
                    </button>
                    {mayDelete(t) && (
                      <button
                        className="btn sm danger"
                        title="Delete"
                        aria-label={`Delete ${t.title}`}
                        disabled={busy}
                        onClick={() => void remove(t)}
                      >
                        ×
                      </button>
                    )}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </aside>
      {listOpen && <button className="chat-threads-scrim" aria-label="Close the chat list" onClick={() => setListOpen(false)} />}

      {opened === null ? (
        <div className="pad label" style={{ flex: 1 }}>Opening the chat…</div>
      ) : (
        <Chat
          key={opened.thread?.chatId ?? "new"}
          token={token}
          venueId={venueId}
          userId={app.me.userId}
          library={app.library}
          overrides={app.overrides}
          canChat={app.me.can["chat:use"]}
          canEdit={app.me.can["spec:edit"]}
          thread={opened.thread}
          history={opened.history}
          subject={subject}
          initialInput={params.prompt}
          headerStart={
            <button className="btn sm chat-threads-toggle" aria-expanded={listOpen} onClick={() => setListOpen((v) => !v)}>
              Chats
            </button>
          }
          onPickSubject={(specId) => {
            // An empty new chat just changes what it is about; one with
            // messages in it stays about what it was, so this starts another.
            if (!thread) go({ kind: "chat", params: { ...(specId ? { specId } : {}), fresh: "1" } }, { replace: true });
            else newChat(specId);
          }}
          onThread={(t, created) => {
            upsert(t);
            setCurrent(t);
            if (created) go({ kind: "chat", params: { chatId: t.chatId } }, { replace: true });
          }}
          onLibraryChanged={() => void app.refreshLibrary()}
          onApply={async (build) => {
            // The batch the chat planned is a house-made ingredient (#180):
            // saved first, so the recipe can pour it.
            const folded = foldBatch(build);
            if (folded.batch) {
              const existing = app.library.ingredients.find((i) => i.ingredientId === folded.batch!.ingredientId);
              await api.saveIngredient(
                app.token,
                folded.batch.ingredientId,
                existing
                  ? { ...existing, build: folded.batch.build }
                  : { name: folded.batch.name, costPerMl: 0, abv: 0, aromaFamily: null, kind: "other", source: "chat", build: folded.batch.build },
              );
            }
            // Where it lands: a new recipe of the subject's item when the chat
            // called it a variant, the subject itself otherwise, or a new item.
            const recipe = subject ? app.library.recipes?.find((r) => r.recipeId === subject.specId) : undefined;
            let recipeId: string;
            if (recipe && build.variant) recipeId = (await api.createRecipe(app.token, recipe.itemId, build.variant)).recipe.recipeId;
            else if (subject) recipeId = subject.specId;
            else recipeId = (await api.createItem(app.token, build.name, "drink")).recipe.recipeId;
            await app.refreshLibrary();
            app.setDraftBuild({ ...build, lines: folded.lines });
            go({ kind: "recipes", params: { specId: recipeId, edit: "1" } });
          }}
        />
      )}
    </div>
  );
}

/** "just now", "5 min ago", "3 h ago", "2 Oct". */
function ago(iso: string, now: Date = new Date()): string {
  const minutes = Math.round((now.getTime() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes)) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} h ago`;
  return new Date(iso).toLocaleDateString("en-AU", { timeZone: venueZone(), day: "numeric", month: "short" });
}
